import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useAdminNotifications } from '../context/AdminNotificationContext';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Clock, Users, Calendar, ChevronLeft, Share2, TrendingUp, HandHeart } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import ApplicationsTab from '../components/admin/professionals/ApplicationsTab';
import ProfessionalsTab from '../components/admin/professionals/ProfessionalsTab';
import WebinarsTab from '../components/admin/professionals/WebinarsTab';
import RecruitTab from '../components/admin/professionals/RecruitTab';
import GrowthTab from '../components/admin/professionals/GrowthTab';
import OversightTab from '../components/admin/oversight/OversightTab';
import FamilyRequestsTab from '../components/admin/requests/FamilyRequestsTab';
import { AttentionStrip } from '../components/admin/AttentionStrip';
import PilotTab from '../components/admin/pilot/PilotTab';

const API = '';

const EMPTY_PRO_FORM = {
  name: '',
  professional_type: 'financial_advisor',
  company: '',
  bio: '',
  specialties: '',
  email: '',
  phone: '',
  website: '',
  location: '',
  picture_url: '',
  is_featured: false,
  member_discount: '',
};

const EMPTY_WEBINAR_FORM = {
  title: '',
  description: '',
  presenter: '',
  presenter_title: '',
  date: '',
  time: '',
  duration_minutes: 60,
  category: '',
  max_attendees: '',
  meeting_link: '',
};

export default function AdminProfessionals() {
  useAuth();
  const { refreshCount: refreshAdminNotifications } = useAdminNotifications();
  const token = localStorage.getItem('session_token');
  const navigate = useNavigate();
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const [activeTab, setActiveTab] = useState(() => new URLSearchParams(window.location.search).get('tab') || 'applications');
  const [professionals, setProfessionals] = useState([]);
  const [pendingApplications, setPendingApplications] = useState([]);
  const [webinars, setWebinars] = useState([]);
  const [pendingWebinars, setPendingWebinars] = useState([]);
  const [signupStats, setSignupStats] = useState(null);
  const [growthStats, setGrowthStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [expandedProf, setExpandedProf] = useState(null);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [formData, setFormData] = useState(EMPTY_PRO_FORM);
  const [webinarForm, setWebinarForm] = useState(EMPTY_WEBINAR_FORM);
  const [activeRequests, setActiveRequests] = useState(0);

  const fetchRequestCount = async () => {
    try {
      const res = await axios.get(`${API}/api/professional-requests/admin?status=active`, authHeaders);
      setActiveRequests(res.data.length);
    } catch (err) {
      console.debug('Could not load family requests');
    }
  };

  // Fetchers
  const fetchGrowthStats = async () => {
    try {
      const res = await axios.get(`${API}/api/admin/growth-stats`, authHeaders);
      setGrowthStats(res.data);
    } catch (err) {
      console.debug('Could not load growth stats');
    }
  };

  const fetchSignupStats = async () => {
    try {
      const res = await axios.get(`${API}/api/directory/admin/signup-stats`, authHeaders);
      setSignupStats(res.data);
    } catch (err) {
      console.debug('Could not load signup stats');
    }
  };

  const fetchPendingApplications = async () => {
    try {
      const res = await axios.get(`${API}/api/directory/admin/pending`, authHeaders);
      setPendingApplications(res.data || []);
    } catch (err) {
      console.debug('Could not load pending applications');
    }
  };

  const fetchProfessionals = async () => {
    try {
      const res = await axios.get(`${API}/api/admin/professionals?include_inactive=${includeInactive}`, authHeaders);
      setProfessionals(res.data || []);
    } catch (err) {
      console.error('Failed to load professionals:', err);
      toast.error('Failed to load professionals');
    } finally {
      setLoading(false);
    }
  };

  const fetchWebinars = async () => {
    try {
      const res = await axios.get(`${API}/api/admin/webinars`, authHeaders);
      setWebinars(res.data || []);
    } catch (err) {
      console.debug('Could not load webinars');
    }
  };

  const fetchPendingWebinars = async () => {
    try {
      const res = await axios.get(`${API}/api/admin/webinars/pending`, authHeaders);
      setPendingWebinars(res.data || []);
    } catch (err) {
      console.debug('Could not load pending webinars');
    }
  };

  useEffect(() => {
    if (token) {
      fetchProfessionals();
      fetchWebinars();
      fetchPendingApplications();
      fetchPendingWebinars();
      fetchSignupStats();
      fetchGrowthStats();
      fetchRequestCount();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, includeInactive]);

  const markNotificationRead = async (profileId) => {
    try {
      const notifs = await axios.get(`${API}/api/directory/admin/notifications?unread_only=true`, authHeaders);
      const relatedNotif = notifs.data.find(n => n.profile_id === profileId);
      if (relatedNotif) {
        await axios.post(`${API}/api/directory/admin/notifications/${relatedNotif.notification_id}/read`, {}, authHeaders);
      }
    } catch (err) {
      console.debug('Could not mark notification as read');
    }
  };

  // Application handlers
  const handleApproveApplication = async (application) => {
    try {
      await axios.post(`${API}/api/directory/admin/professionals/${application.profile_id}/decision`, { action: 'approve' }, authHeaders);
      toast.success(`${application.name} has been approved!`);
      fetchPendingApplications();
      await markNotificationRead(application.profile_id);
      refreshAdminNotifications();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve application');
    }
  };

  const handleRejectApplication = async (application) => {
    const reason = window.prompt('Enter a reason for rejection (required)');
    if (!reason) return;
    try {
      await axios.post(`${API}/api/directory/admin/professionals/${application.profile_id}/decision`, { action: 'reject', reason }, authHeaders);
      toast.success('Application rejected');
      fetchPendingApplications();
      await markNotificationRead(application.profile_id);
      refreshAdminNotifications();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject application');
    }
  };

  // Professional handlers
  const handleSubmitProfessional = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        specialties: formData.specialties.split(',').map(s => s.trim()).filter(s => s),
      };
      if (editingItem) {
        await axios.patch(`${API}/api/admin/professionals/${editingItem.professional_id}`, data, authHeaders);
        toast.success('Professional updated successfully');
      } else {
        await axios.post(`${API}/api/admin/professionals`, data, authHeaders);
        toast.success('Professional created successfully');
      }
      setShowForm(false);
      setEditingItem(null);
      setFormData(EMPTY_PRO_FORM);
      fetchProfessionals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save professional');
    }
  };

  const handleDeleteProfessional = async (professional, permanent = false) => {
    if (!window.confirm(permanent ? 'Permanently delete this professional?' : 'Deactivate this professional?')) return;
    try {
      await axios.delete(`${API}/api/admin/professionals/${professional.professional_id}?permanent=${permanent}`, authHeaders);
      toast.success(permanent ? 'Professional deleted' : 'Professional deactivated');
      fetchProfessionals();
    } catch (error) {
      toast.error('Failed to delete professional');
    }
  };

  const handleActivateProfessional = async (professional) => {
    try {
      await axios.post(`${API}/api/admin/professionals/${professional.professional_id}/activate`, {}, authHeaders);
      toast.success('Professional activated');
      fetchProfessionals();
    } catch (error) {
      toast.error('Failed to activate professional');
    }
  };

  const openEditProfessional = (professional) => {
    setFormData({
      name: professional.name || '',
      professional_type: professional.professional_type || 'financial_advisor',
      company: professional.company || '',
      bio: professional.bio || '',
      specialties: (professional.specialties || []).join(', '),
      email: professional.email || '',
      phone: professional.phone || '',
      website: professional.website || '',
      location: professional.location || '',
      picture_url: professional.picture_url || '',
      is_featured: professional.is_featured || false,
      member_discount: professional.member_discount || '',
    });
    setEditingItem(professional);
    setShowForm(true);
  };

  const cancelProfessionalForm = () => {
    setShowForm(false);
    setEditingItem(null);
    setFormData(EMPTY_PRO_FORM);
  };

  // Webinar handlers
  const handleSubmitWebinar = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...webinarForm,
        max_attendees: webinarForm.max_attendees ? parseInt(webinarForm.max_attendees) : null,
        duration_minutes: parseInt(webinarForm.duration_minutes),
      };
      if (editingItem) {
        await axios.patch(`${API}/api/admin/webinars/${editingItem.webinar_id}`, data, authHeaders);
        toast.success('Webinar updated successfully');
      } else {
        await axios.post(`${API}/api/admin/webinars`, data, authHeaders);
        toast.success('Webinar created successfully');
      }
      setShowForm(false);
      setEditingItem(null);
      setWebinarForm(EMPTY_WEBINAR_FORM);
      fetchWebinars();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save webinar');
    }
  };

  const handleDeleteWebinar = async (webinar) => {
    if (!window.confirm('Delete this webinar?')) return;
    try {
      await axios.delete(`${API}/api/admin/webinars/${webinar.webinar_id}`, authHeaders);
      toast.success('Webinar deleted');
      fetchWebinars();
    } catch (error) {
      toast.error('Failed to delete webinar');
    }
  };

  const handleApproveWebinar = async (webinarId) => {
    try {
      await axios.post(`${API}/api/admin/webinars/${webinarId}/approve`, {}, authHeaders);
      toast.success('Webinar approved!');
      fetchPendingWebinars();
      fetchWebinars();
      refreshAdminNotifications();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve webinar');
    }
  };

  const handleRejectWebinar = async (webinarId) => {
    const reason = window.prompt('Optional: Provide a reason for rejection (or leave blank):');
    try {
      await axios.post(`${API}/api/admin/webinars/${webinarId}/reject?reason=${encodeURIComponent(reason || '')}`, {}, authHeaders);
      toast.success('Webinar rejected');
      fetchPendingWebinars();
      fetchWebinars();
      refreshAdminNotifications();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject webinar');
    }
  };

  const openEditWebinar = (webinar) => {
    setWebinarForm({
      title: webinar.title || '',
      description: webinar.description || '',
      presenter: webinar.presenter || '',
      presenter_title: webinar.presenter_title || '',
      date: webinar.date || '',
      time: webinar.time || '',
      duration_minutes: webinar.duration_minutes || 60,
      category: webinar.category || '',
      max_attendees: webinar.max_attendees || '',
      meeting_link: webinar.meeting_link || '',
    });
    setEditingItem(webinar);
    setShowForm(true);
  };

  const cancelWebinarForm = () => {
    setShowForm(false);
    setEditingItem(null);
    setWebinarForm(EMPTY_WEBINAR_FORM);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 text-white">
      {/* Header */}
      <div className="bg-slate-800/50 border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/dashboard')}
                className="text-slate-300 border-slate-600 hover:bg-slate-700 hover:text-white hover:border-slate-500"
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Back to Dashboard
              </Button>
              <div>
                <h1 className="text-xl font-bold text-white">Admin Panel</h1>
                <p className="text-sm text-slate-400">Manage Professionals & Webinars</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <AttentionStrip onOpenTab={setActiveTab} refreshKey={activeRequests + pendingApplications.length} />
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-slate-800 border border-slate-700 mb-6 flex w-full justify-start overflow-x-auto">
            <TabsTrigger value="applications" className="data-[state=active]:bg-amber-600 relative">
              <Clock className="h-4 w-4 mr-2" />
              Pending Applications
              {pendingApplications.length > 0 && (
                <span className="ml-2 bg-red-500 text-white text-xs font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                  {pendingApplications.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="professionals" className="data-[state=active]:bg-amber-600">
              <Users className="h-4 w-4 mr-2" />
              Professionals ({professionals.length})
            </TabsTrigger>
            <TabsTrigger value="webinars" className="data-[state=active]:bg-amber-600 relative">
              <Calendar className="h-4 w-4 mr-2" />
              Webinars ({webinars.length})
              {pendingWebinars.length > 0 && (
                <span className="absolute -top-1 -right-1 px-1.5 py-0.5 text-xs bg-red-500 text-white rounded-full">
                  {pendingWebinars.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="outreach" className="data-[state=active]:bg-amber-600">
              <Share2 className="h-4 w-4 mr-2" />
              Recruit
            </TabsTrigger>
            <TabsTrigger value="growth" className="data-[state=active]:bg-amber-600">
              <TrendingUp className="h-4 w-4 mr-2" />
              Growth
            </TabsTrigger>
            <TabsTrigger value="oversight" className="data-[state=active]:bg-amber-600" data-testid="oversight-tab-trigger">
              Oversight
            </TabsTrigger>
            <TabsTrigger value="requests" className="data-[state=active]:bg-amber-600" data-testid="family-requests-tab-trigger">
              <HandHeart className="h-4 w-4 mr-2" />
              Family Requests
              {activeRequests > 0 && (
                <span className="ml-2 bg-red-500 text-white text-xs font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1" data-testid="family-requests-count">
                  {activeRequests}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="pilot" className="data-[state=active]:bg-emerald-600" data-testid="pilot-tab-trigger">
              Pilot
            </TabsTrigger>
          </TabsList>

          <TabsContent value="pilot">
            <PilotTab />
          </TabsContent>

          <TabsContent value="requests">
            <FamilyRequestsTab onChanged={() => { fetchRequestCount(); refreshAdminNotifications(); }} />
          </TabsContent>

          <TabsContent value="oversight">
            <OversightTab />
          </TabsContent>

          <TabsContent value="applications">
            <ApplicationsTab
              pendingApplications={pendingApplications}
              expandedProf={expandedProf}
              setExpandedProf={setExpandedProf}
              onApprove={handleApproveApplication}
              onReject={handleRejectApplication}
            />
          </TabsContent>

          <TabsContent value="professionals">
            <ProfessionalsTab
              professionals={professionals}
              showForm={showForm && activeTab === 'professionals'}
              formData={formData}
              setFormData={setFormData}
              editingItem={editingItem}
              expandedProf={expandedProf}
              setExpandedProf={setExpandedProf}
              includeInactive={includeInactive}
              setIncludeInactive={setIncludeInactive}
              onAddNew={() => { setShowForm(true); setEditingItem(null); setFormData(EMPTY_PRO_FORM); }}
              onEdit={openEditProfessional}
              onSubmit={handleSubmitProfessional}
              onCancel={cancelProfessionalForm}
              onDeactivate={(prof) => handleDeleteProfessional(prof, false)}
              onActivate={handleActivateProfessional}
            />
          </TabsContent>

          <TabsContent value="webinars">
            <WebinarsTab
              webinars={webinars}
              pendingWebinars={pendingWebinars}
              showForm={showForm && activeTab === 'webinars'}
              webinarForm={webinarForm}
              setWebinarForm={setWebinarForm}
              editingItem={editingItem}
              onAddNew={() => { setShowForm(true); setEditingItem(null); setWebinarForm(EMPTY_WEBINAR_FORM); }}
              onEdit={openEditWebinar}
              onSubmit={handleSubmitWebinar}
              onCancel={cancelWebinarForm}
              onDelete={handleDeleteWebinar}
              onApprovePending={handleApproveWebinar}
              onRejectPending={handleRejectWebinar}
            />
          </TabsContent>

          <TabsContent value="outreach">
            <RecruitTab signupStats={signupStats} />
          </TabsContent>

          <TabsContent value="growth">
            <GrowthTab growthStats={growthStats} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
